// src/hooks/useDimensions.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createDimension,
  deleteDimension,
  getDimension,
  listDimensions,
  setDimensionStatus,
  updateDimension,
} from "@/services/dimensions";
import type { GroCrudScope } from "@/lib/groCrudScope";
import type { DimensionUpsertIn } from "@/types/dimensions";

export function useDimensions(
  params?: { q?: string; risk_theme?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  return useQuery({
    queryKey: ["dimensions", scope, params?.q ?? "", params?.risk_theme ?? "", params?.is_active],
    queryFn: () => listDimensions(params, scope),
  });
}

export function useDimensionDetail(id?: string, scope: GroCrudScope = "client") {
  return useQuery({
    queryKey: ["dimension_detail", scope, id],
    queryFn: () => getDimension(id as string, scope),
    enabled: !!id,
  });
}

export function useCreateDimension(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DimensionUpsertIn) => createDimension(body, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dimensions", scope] }),
  });
}

export function useUpdateDimension(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DimensionUpsertIn }) => updateDimension(id, body, scope),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["dimensions", scope] });
      qc.invalidateQueries({ queryKey: ["dimension_detail", scope, variables.id] });
    },
  });
}

export function useSetDimensionStatus(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setDimensionStatus(id, is_active, scope),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["dimensions", scope] });
      qc.invalidateQueries({ queryKey: ["dimension_detail", scope, variables.id] });
    },
  });
}

export function useDeleteDimension(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDimension(id, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dimensions", scope] }),
  });
}
